import React from "react";
import { Link } from "react-router-dom";
import { Anchor, Anchor as AnchorIcon, Sparkles, Star, Users, Wine, Wine as WineIcon, Music, Music as MusicIcon, Camera, Phone, ArrowRight, MapPin, Calendar, ChevronRight, Ship as ShipIcon, Sun as SunIcon, Utensils as UtensilsIcon, Wifi as WifiIcon, Shield as ShieldIcon } from "lucide-react";
import { CONTACT, BOAT_FACTS, GALLERY, TESTIMONIALS, MAGIC_PHOTOS } from "../data/cruiseData";
import { SEO_PAGES } from "../data/seoPages";
import EnquiryForm from "../components/EnquiryForm";
import HeroSlider from "../components/HeroSlider";
import HeroStory from "../components/HeroStory";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import BookingForm from "../components/BookingForm";
import TestimonialsSection from "../components/TestimonialsSection";
import MagicComesAlive from "../components/MagicComesAlive";
import FAQSection from "../components/FAQSection";
import SEOHead from "../components/SEOHead";

export default function Home() {
 React.useEffect(() => { document.title = "Magic Cruises | Sydney Harbour Cruises · Party Boat Hire · NYE Fireworks"; }, []);
 const featured = SEO_PAGES.slice(0, 6);
 const [availISO, setAvailISO] = React.useState(null);

 return (
 <main data-testid="home-page">
 <SEOHead
 title="Magic Cruises | Sydney Harbour Cruises · Party Boat Hire · NYE Fireworks"
 description="Sydney Harbour cruises on the iconic MV Sydney Magic. Private boat hire, NYE fireworks, weddings, corporate events, hens parties & birthday cruises. 120 guests · 2 decks · 20+ years."
 image={MAGIC_PHOTOS.hensBridal}
 type="TouristAttraction"
 schemaExtra={{ knowsAbout: ["Sydney Harbour Cruises","Private Boat Hire","NYE Cruises","Wedding Cruises","Corporate Events","Hens Parties","BYO Cruises"] }}
 />
 {/* HERO STORY, Instagram-style themed slideshow */}
 <HeroStory />
 {/* Hidden legacy hero (kept for testids reference, not rendered) */}
 <div className="hidden">
 <section className="relative min-h-screen flex items-end pb-12 lg:pb-20 overflow-hidden">
 <HeroSlider />
 {/* Extra dark overlay (reference site is much darker than ours was) */}
 <div className="absolute inset-0 bg-[hsla(220,60%,8%,0.55)] pointer-events-none" />
 <div className="relative mx-auto max-w-7xl px-5 lg:px-10 w-full grid lg:grid-cols-12 gap-10 items-end">
 <div className="lg:col-span-8">
 <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsla(220,60%,10%,0.55)] border border-white/15 backdrop-blur-sm rise mb-5">
 <span className="text-yellow-300 text-sm">✨</span>
 <span className="text-white text-[10px] uppercase tracking-[0.28em] font-bold">NYE Fireworks Cruise</span>
 </div>
 <div className="purple-eyebrow text-[12px] uppercase tracking-[0.32em] font-bold rise rise-d1">New Year's Eve 2026</div>
 <h1 className="font-display text-5xl sm:text-6xl lg:text-[7rem] text-white leading-[1.02] rise rise-d1 mt-3 max-w-5xl" data-testid="hero-headline">
 Sydney Harbour<br/><em className="italic-gradient not-italic">like never before.</em>
 </h1>
 <p className="font-script text-3xl lg:text-4xl text-[hsl(200,95%,80%)] mt-3 rise rise-d2">"Fireworks. Front row."</p>
 <p className="text-white/85 text-base lg:text-lg max-w-2xl mt-5 rise rise-d3 leading-relaxed">Step aboard <strong className="text-white">Magic</strong>, Sydney's iconic luxury catamaran for corporate cocktail nights, NYE fireworks parties, milestone birthdays &amp; private celebrations on the world's most beautiful harbour.</p>

 <div className="flex flex-wrap gap-2.5 mt-7 rise rise-d3">
 <span className="chip-event chip-corp">💼 Corporate</span>
 <span className="chip-event chip-nye">✨ NYE & Vivid</span>
 <span className="chip-event chip-birthday">🎂 Birthdays</span>
 <span className="chip-event chip-hens">👑 Hens</span>
 <span className="chip-event chip-private">⭐ Private</span>
 </div>

 <div className="flex flex-wrap gap-3 mt-7 rise rise-d4">
 <Link to="/contact" className="btn-yellow" data-testid="hero-enquire-btn">Get A Quote Now <ArrowRight className="w-4 h-4" /></Link>
 <Link to="/packages" className="btn-ghost text-white border-white hover:bg-white hover:text-ink" data-testid="hero-packages-btn">View Cruise Packages</Link>
 </div>

 <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-10 rise rise-d4 max-w-2xl">
 <div className="flex items-center gap-3">
 <div className="flex">{Array.from({length:5}).map((_,j)=><Star key={j} className="w-4 h-4 fill-[hsl(48,100%,60%)] text-[hsl(48,100%,60%)]" />)}</div>
 <div><div className="font-display text-lg text-white leading-none">5.0 Rated</div><div className="text-[10px] uppercase tracking-wider text-white/65 mt-1 font-bold">500+ Happy Guests</div></div>
 </div>
 <div className="flex items-center gap-3">
 <Users className="w-5 h-5 text-[hsl(200,95%,75%)]" />
 <div><div className="font-display text-lg text-white leading-none">Up to 120 Guests</div><div className="text-[10px] uppercase tracking-wider text-white/65 mt-1 font-bold">Private Charter</div></div>
 </div>
 <div className="flex items-center gap-3">
 <Calendar className="w-5 h-5 text-[hsl(200,95%,75%)]" />
 <div><div className="font-display text-lg text-white leading-none">365 Days a Year</div><div className="text-[10px] uppercase tracking-wider text-white/65 mt-1 font-bold">Available 7 Days</div></div>
 </div>
 </div>
 </div>

 {/* Right-side instagram-style card */}
 <div className="lg:col-span-4 hidden lg:block">
 <div className="bg-white rounded-2xl border border-white/30 p-4 shadow-2xl rise rise-d4 -rotate-2" style={{boxShadow:'0 30px 80px -20px rgba(0,0,0,0.5)'}}>
 <div className="flex items-center gap-2 mb-3">
 <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[hsl(280,75%,60%)] to-[hsl(335,90%,60%)] flex items-center justify-center text-white text-sm font-bold">M</div>
 <div className="flex-1"><div className="font-bold text-sm text-ink">@magic.cruises</div><div className="text-[10px] text-ink-mute uppercase tracking-wider">Sydney Harbour · Live</div></div>
 <div className="text-ink-mute">···</div>
 </div>
 <div className="grid grid-cols-2 gap-1 rounded-xl overflow-hidden">
 <img src={MAGIC_PHOTOS.championToast} alt="" className="aspect-square object-cover" />
 <img src={MAGIC_PHOTOS.corporateLounge} alt="" className="aspect-square object-cover" />
 <img src={MAGIC_PHOTOS.fireworksCollage} alt="" className="aspect-square object-cover" />
 <div className="relative aspect-square">
 <img src={MAGIC_PHOTOS.hensBridal} alt="" className="absolute inset-0 w-full h-full object-cover" />
 <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-[hsla(335,90%,60%,0.95)] text-white text-[9px] uppercase tracking-wider font-bold">Live</div>
 </div>
 </div>
 <div className="flex items-center gap-3 mt-3 text-ink"><span>♥</span><span>💬</span><span>🥂</span><span className="ml-auto text-xs font-bold">2.4k likes</span></div>
 <div className="text-xs text-ink mt-2"><strong>@magic.cruises</strong> <span className="text-ink-mute">Fireworks. Front row. #SydneyHarbour #NYE2026</span></div>
 </div>
 </div>
 </div>
 </section>
 </div>
 {/* end hidden legacy hero */}

 {/* TRUST STRIP, dark navy with giant yellow numbers */}
 <section className="bg-[hsl(220,60%,14%)] border-y border-[hsl(220,60%,10%)]" data-testid="trust-strip">
 <div className="mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
 {[
 { n: "20+", l: "Years on Sydney Harbour" },
 { n: "5,000+", l: "Cruises Hosted" },
 { n: "500+", l: "5-Star Reviews" },
 { n: "120", l: "Guest Capacity" },
 ].map((s, i) => (
 <div key={i} className="text-center text-white">
 <div className="text-5xl md:text-6xl font-black bg-gradient-to-br from-amber-300 to-amber-500 bg-clip-text text-transparent mb-2 leading-none tracking-tight">{s.n}</div>
 <div className="text-xs md:text-sm uppercase tracking-widest text-white/70 font-bold">{s.l}</div>
 </div>
 ))}
 </div>
 </section>

 {/* INTRO */}
 <section className="section-orbs section-orbs--cool mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
 <div className="text-center mb-14">
 <div className="eyebrow justify-center">Explore Magic</div>
 <h2 className="font-display text-5xl lg:text-8xl mt-5 leading-[0.95] text-ink uppercase">
 Sydney's most<br/><em className="not-italic italic text-[hsl(var(--pink))]" style={{fontStyle:'italic'}}>loved harbour cruises.</em>
 </h2>
 <p className="text-ink-soft text-lg max-w-3xl mx-auto mt-6 leading-relaxed">From sunset Sydney Harbour cruises to corporate charters and the famous NYE fireworks cruise, find your perfect day on the water.</p>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
 {[
 { label: "Sydney Harbour Cruises", desc: "Sunset, day & evening cruises past the Opera House and Harbour Bridge.", slug: "private-harbour-cruises-sydney" },
 { label: "Boat Cruises Sydney Harbour", desc: "Private boat hire and charter cruises for every kind of celebration.", slug: "boat-party-sydney-harbour" },
 { label: "Corporate Cruise Sydney", desc: "Christmas parties, EOFY, product launches and client hospitality.", slug: "corporate-cruises-sydney-harbour" },
 { label: "NYE Cruise Sydney", desc: "Front-row Sydney Harbour fireworks on Sydney's most exclusive NYE charter.", slug: "nye-cruises-sydney-harbour" },
 { label: "BYO Cruise Sydney", desc: "The only Sydney private boat hire with BYO drinks and zero corkage.", slug: "byo-cruise-sydney-harbour" },
 { label: "Hens & Birthday Cruises", desc: "Sydney Harbour hens parties, milestone birthdays and bridal celebrations.", slug: "hens-party-cruises-sydney-harbour" },
 ].map((c, i) => (
 <Link to={`/cruises/${c.slug}`} key={i} className="group bg-white rounded-xl border border-[hsla(220,20%,82%,0.6)] p-6 hover:border-[hsl(var(--pink))] transition-colors card-pop">
 <div className="font-display text-xl text-ink leading-tight">{c.label}</div>
 <div className="text-sm text-ink-soft mt-3 leading-snug">{c.desc}</div>
 <div className="mt-4 text-pink text-xs uppercase tracking-[0.18em] font-bold flex items-center gap-1 group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-3 h-3" /></div>
 </Link>
 ))}
 </div>
 </section>

 {/* CRUISE PACKAGES, Big editorial cards */}
 <section className="section-orbs section-orbs--sunset mx-auto max-w-7xl px-5 lg:px-10 pb-20">
 <div className="text-center mb-14">
 <div className="eyebrow justify-center">Cruise Packages</div>
 <h2 className="font-display text-5xl lg:text-7xl mt-5 text-ink leading-[1.05]">
 Charter Magic for any<br/><em>occasion you can dream of.</em>
 </h2>
 <p className="text-ink-soft text-lg max-w-2xl mx-auto mt-6 leading-relaxed">From intimate sunset cruises to spectacular New Year's Eve celebrations, every Magic Cruises Sydney Harbour charter is fully private and tailored to your vision.</p>
 </div>
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
 {featured.map((p) => (
 <Link to={`/cruises/${p.slug}`} key={p.slug} className="group card-pop bg-white rounded-2xl border border-[hsla(220,20%,82%,0.5)] overflow-hidden" data-testid={`featured-${p.slug}`}>
 <div className="aspect-[4/3] overflow-hidden relative">
 <img src={p.image} alt={p.h1} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
 <span className="absolute top-4 left-4 sticker bg-white/95">{p.tag}</span>
 </div>
 <div className="p-5">
 <div className="font-display text-2xl text-ink leading-tight">{p.h1}</div>
 <div className="text-sm text-ink-mute mt-2 line-clamp-2">{p.headline}</div>
 <div className="mt-4 text-pink text-xs uppercase tracking-[0.18em] font-bold flex items-center gap-1">Enquire now <ArrowRight className="w-3 h-3" /></div>
 </div>
 </Link>
 ))}
 </div>
 <div className="text-center mt-10">
 <Link to="/cruises" className="btn-ghost" data-testid="all-cruises-btn">All Cruise Types <ArrowRight className="w-3.5 h-3.5" /></Link>
 </div>
 </section>

 {/* MAGIC COMES ALIVE, party energy section */}
 <MagicComesAlive />

 {/* THE VESSEL, polished blue gradient + amber stats + feature grid */}
 <section className="relative overflow-hidden text-white" data-testid="vessel-section">
 <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, hsl(220 75% 22%) 0%, hsl(215 80% 32%) 45%, hsl(210 85% 42%) 100%)" }} />
 <div className="absolute -top-32 right-0 w-[40rem] h-[40rem] rounded-full bg-sky-400/15 blur-3xl pointer-events-none" />
 <div className="absolute -bottom-32 left-0 w-[40rem] h-[40rem] rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />

 <div className="max-w-7xl mx-auto px-6 relative z-10 py-24 lg:py-32">
 <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
 {/* LEFT: copy + stats */}
 <div>
 <div className="inline-flex items-center gap-2 text-sky-300 text-[11px] uppercase tracking-[0.3em] font-bold">─ Meet The Vessel ─</div>
 <h2 className="text-white text-5xl md:text-6xl lg:text-7xl font-black mt-6 leading-[0.95] tracking-tight" data-testid="vessel-headline">
 The <span className="italic font-light text-amber-300">Magic</span>
 <span className="block">Catamaran</span>
 </h2>
 <div className="mt-8 space-y-5 text-white/85 text-base md:text-lg leading-relaxed max-w-xl">
 <p>Magic is a stunning 60-foot luxury catamaran purpose-built for Sydney Harbour celebrations. Twin hulls deliver rock-steady cruising while three generous decks give your guests room to dance, dine and soak up the world's most beautiful harbour skyline.</p>
 <p>From the climate-controlled main saloon to the open-air sky deck with sweeping views of the Sydney Opera House and Harbour Bridge, Magic is built to make every event feel completely your own.</p>
 </div>

 {/* Yellow Stats */}
 <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-white/15 max-w-md" data-testid="vessel-stats">
 {[{n:"100", l:"Max Guests"}, {n:"60ft", l:"Length"}, {n:"3", l:"Levels"}].map((s, i) => (
 <div key={i}>
 <div className="text-4xl font-black text-amber-300">{s.n}</div>
 <div className="text-xs uppercase tracking-wider text-white/60 mt-1">{s.l}</div>
 </div>
 ))}
 </div>
 </div>

 {/* RIGHT: hero photo with rounded card frame */}
 <div className="relative">
 <div className="absolute -inset-4 bg-gradient-to-br from-sky-400/30 to-amber-400/30 rounded-3xl blur-2xl" />
 <img src={MAGIC_PHOTOS.magicCruising} alt="The Magic catamaran charter boat cruising on Sydney Harbour" className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[5/4]" />
 </div>
 </div>

 {/* FEATURE GRID, 8 cards (2 rows × 4) */}
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-16" data-testid="vessel-features">
 {[
 { Icon: ShipIcon, t: "Spacious Catamaran", b: "Twin-hull stability with three expansive levels for guests" },
 { Icon: SunIcon, t: "Two Open-Air Decks", b: "Sun-soaked top deck with 360° Sydney Harbour panoramas" },
 { Icon: UtensilsIcon, t: "Full Galley & Bar", b: "On-board catering kitchen and licensed full-service bar" },
 { Icon: MusicIcon, t: "Premium Sound System", b: "Bluetooth-ready speakers across all decks for your playlist" },
 { Icon: WineIcon, t: "Beverage Packages", b: "Tailored drinks lists from house pours to premium spirits" },
 { Icon: WifiIcon, t: "Climate-Controlled Saloon", b: "Air-conditioned lower deck with floor-to-ceiling windows" },
 { Icon: ShieldIcon, t: "Fully Survey Approved", b: "NSW Maritime certified crew and full safety equipment" },
 { Icon: AnchorIcon, t: "Iconic Departure Points", b: "King Street Wharf, Darling Harbour & Circular Quay" },
 ].map((f, i) => {
 const Icon = f.Icon;
 return (
 <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-sky-400/50 transition-all" data-testid={`vessel-feature-${i}`}>
 <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center mb-4">
 <Icon className="w-6 h-6 text-white" />
 </div>
 <h3 className="font-bold text-lg mb-2">{f.t}</h3>
 <p className="text-sm text-white/70 leading-relaxed">{f.b}</p>
 </div>
 );
 })}
 </div>

  </div>
 </section>

 {/* NYE STRIP */}
 <section className="relative border-y border-ink overflow-hidden">
 <div className="absolute inset-0"><img src={MAGIC_PHOTOS.nyeFireworks} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--ink))]/85 via-[hsl(var(--ink))]/65 to-[hsl(var(--ink))]/40" /></div>
 <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
 <div className="max-w-2xl">
 <div className="text-pink text-[11px] uppercase tracking-[0.3em] font-bold">2026 · New Year's Eve</div>
 <h2 className="text-white text-4xl lg:text-7xl font-black mt-4 leading-[1] tracking-tight">Front row to Sydney's <span className="italic font-light text-amber-300">fireworks</span>.</h2>
 <p className="text-white/85 mt-5 leading-relaxed">Magic Cruises is a regular participant in the famous NYE Sydney Harbour Light Parade. Two fireworks shows, full beverage package, premium canapés and DJ all night. Adult $1,250 / Child $950.</p>
 <div className="flex flex-wrap gap-3 mt-8">
 <Link to="/cruises/nye-cruises-sydney-harbour" className="btn-magic"><Calendar className="w-3.5 h-3.5" /> NYE 2026 Details</Link>
 <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border-2 border-white/40 text-white px-6 py-3 rounded-full font-bold text-sm tracking-wide hover:bg-white hover:text-ink transition-all">Group Enquiry</Link>
 </div>
 </div>
 </div>
 </section>

 {/* WHERE WE CRUISE, split layout: empty/full polaroids + 8 location cards */}
      <section className="relative bg-slate-50 py-24 lg:py-32" data-testid="where-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-[28rem] h-[28rem] rounded-full bg-sky-200/40 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-[28rem] h-[28rem] rounded-full bg-amber-200/40 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative grid grid-cols-2 gap-4">
                <div className="absolute -inset-6 bg-gradient-to-br from-sky-200/40 to-amber-200/40 rounded-3xl blur-2xl pointer-events-none" />
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img src={MAGIC_PHOTOS.magicInterior} alt="Magic catamaran interior cabin, empty, showing seating and harbour views" className="w-full h-full object-cover aspect-[4/5]" />
                  <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-bold tracking-widest text-blue-950 uppercase">Empty</div>
                </div>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img src={MAGIC_PHOTOS.birthdayFull} alt="Magic catamaran interior full of guests celebrating a birthday" className="w-full h-full object-cover aspect-[4/5]" />
                  <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-amber-400/95 backdrop-blur text-[10px] font-bold tracking-widest text-blue-950 uppercase">Full</div>
                </div>
              </div>
              <p className="relative mt-4 text-center text-xs font-semibold tracking-wider text-slate-500 uppercase">Same space, empty & in full party mode</p>
            </div>
            <div className="order-1 lg:order-2">
              <div className="text-sky-600 font-bold tracking-[0.3em] text-xs uppercase mb-4">─ Where We Cruise ─</div>
              <h2 className="text-4xl md:text-5xl font-black text-blue-950 mb-5 tracking-tight leading-[1.05]">
                Sydney's most spectacular
                <span className="block italic font-light text-sky-600">harbour route.</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">Every Magic charter is fully customisable. Cruise past the icons, anchor for a swim, or sail all the way to Manly, your skipper, your route, your day.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { name: "Sydney Opera House", desc: "Glide past the iconic sails for jaw-dropping photos." },
                  { name: "Sydney Harbour Bridge", desc: "Cruise directly under the world-famous coathanger." },
                  { name: "Fort Denison", desc: "Circle the historic island fort in the middle of the harbour." },
                  { name: "Shark Island", desc: "Anchor at one of Sydney's prettiest swim spots." },
                  { name: "Watsons Bay", desc: "Sail east past million-dollar mansions to The Gap." },
                  { name: "Manly Beach", desc: "Cruise across the heads with ocean views to Manly." },
                  { name: "Rose Bay", desc: "Drift past seaplanes and Sydney's most exclusive marinas." },
                  { name: "Darling Harbour", desc: "Depart from the heart of the city at King Street Wharf." },
                ].map((l, i) => (
                  <div key={i} className="flex gap-3 p-4 rounded-xl bg-white border border-slate-100 hover:border-sky-300 hover:shadow-md transition-all" data-testid={`where-loc-${i}`}>
                    <MapPin className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold text-blue-950 text-sm">{l.name}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{l.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

 {/* INCLUDED */}
 <section className="mx-auto max-w-7xl px-5 lg:px-10 pb-20">
 <div className="eyebrow">What's on board</div>
 <h2 className="font-display text-4xl lg:text-6xl mt-3 mb-10 text-ink leading-[1.05]">Everything for a legendary night.</h2>
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
 {[
 { icon: Wine, t: "Fully licensed bar", d: "Beverage package $15.50 pp/hr · spirits via tab" },
 { icon: Star, t: "Onboard chef", d: "Canapés $30, $65 pp · Buffets up to $95 pp" },
 { icon: Music, t: "DJ + Sound", d: "Add a pro DJ for $550 · 2-deck sound" },
 { icon: Camera, t: "Photographer", d: "Capture every moment · $650" },
 ].map((s, i) => (
 <div key={i} className="bg-white rounded-2xl border border-[hsla(222,20%,80%,0.5)] p-6 card-pop">
 <div className="w-10 h-10 rounded-full bg-[hsla(335,100%,58%,0.1)] flex items-center justify-center text-pink"><s.icon className="w-5 h-5" /></div>
 <div className="font-display text-xl text-ink mt-4">{s.t}</div>
 <div className="text-sm text-ink-mute mt-2 leading-relaxed">{s.d}</div>
 </div>
 ))}
 </div>
 </section>

 {/* GALLERY MASONRY */}
 <section className="mx-auto max-w-7xl px-5 lg:px-10 py-12">
 <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
 <div><div className="eyebrow">The Magic Gallery</div><h2 className="font-display text-4xl lg:text-6xl mt-3 text-ink leading-[1.05]">Real photos, one real boat.</h2></div>
 <Link to="/gallery" className="btn-ghost">View All</Link>
 </div>
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
 {GALLERY.slice(0, 8).map((g, i) => (
 <div key={i} className={`plate ${i === 0 ? "row-span-2 col-span-2 aspect-square lg:aspect-auto" : "aspect-square"} card-pop`}><img src={g} alt="" className="absolute inset-0 w-full h-full object-cover" /></div>
 ))}
 </div>
 </section>

 {/* TESTIMONIALS, photo backdrop + filterable */}
 <TestimonialsSection limit={4} />

 {/* AVAILABILITY, Check availability & book your Magic moment */}
 <section className="relative overflow-hidden" id="availability" data-testid="home-availability">
 <div className="absolute inset-0 pointer-events-none">
 <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #ffffff 0%, hsl(210, 60%, 97%) 40%, hsl(48, 80%, 95%) 100%)" }} />
 <div className="absolute -top-32 -left-32 w-[36rem] h-[36rem] rounded-full bg-sky-300/25 blur-3xl" />
 <div className="absolute -bottom-40 -right-32 w-[40rem] h-[40rem] rounded-full bg-amber-300/25 blur-3xl" />
 </div>
 <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
 <div className="text-center max-w-3xl mx-auto">
 <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] font-bold text-pink uppercase">
 <span className="w-6 h-px bg-pink/60" /> Live Booking <span className="w-6 h-px bg-pink/60" />
 </div>
 <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-ink mt-5 leading-[1.05]">
 Check availability &amp;<em className="italic font-light text-pink block">book your Magic moment.</em>
 </h2>
 <p className="text-ink-soft mt-5 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
 Real-time availability for the Magic catamaran on Sydney Harbour. Pick a date, choose your time slot, and reserve instantly.
 </p>
 </div>

 <div className="mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
 <AvailabilityCalendar
 hideCTA={true}
 selectedISO={availISO}
 onDateSelect={setAvailISO}
 />
 <BookingForm selectedDate={availISO} onClear={() => setAvailISO(null)} />
 </div>
 </div>
 </section>

 {/* FAQ */}
 <FAQSection />

 {/* ENQUIRY */}
 <section id="enquire" className="section-orbs section-orbs--cool mx-auto max-w-7xl px-5 lg:px-10 py-24">
 <div className="grid lg:grid-cols-5 gap-12">
 <div className="lg:col-span-2">
 <div className="eyebrow">Get In Touch</div>
 <h2 className="font-display text-4xl lg:text-6xl mt-3 leading-[1.05] text-ink">Check availability &amp; <span className="text-pink italic">book your Magic moment</span>.</h2>
 <p className="text-ink-soft mt-5 leading-relaxed">Tell us your date, group size and vibe, we'll send a same-day quote with charter pricing locked in.</p>
 <div className="mt-8 space-y-3 text-sm font-medium">
 <a href={`tel:${CONTACT.phone1}`} className="flex items-center gap-3 text-ink hover:text-pink"><Phone className="w-4 h-4 text-pink" /> {CONTACT.phone1}</a>
 <a href={`tel:${CONTACT.phone2}`} className="flex items-center gap-3 text-ink hover:text-pink"><Phone className="w-4 h-4 text-pink" /> {CONTACT.phone2}</a>
 <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-ink hover:text-pink"><Sparkles className="w-4 h-4 text-pink" /> {CONTACT.email}</a>
 <div className="flex items-center gap-3 text-ink-soft"><MapPin className="w-4 h-4 text-pink" /> {CONTACT.embarkation}</div>
 </div>
 </div>
 <div className="lg:col-span-3"><EnquiryForm sourcePage="home" /></div>
 </div>
 </section>
 </main>
 );
}
